import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list, type Role } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Severity, Skel } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'

/**
 * Edge security audit (part 40).
 *
 * One press of "สแกนเดี๋ยวนี้" makes the API issue a real HTTPS request to the URL in the form,
 * records the redirect chain without following it blindly, and grades the answer: TLS version and
 * cipher, HSTS/CSP/X-Frame-Options/Referrer-Policy/Permissions-Policy, redirect policy and cookie
 * flags. Every check — including the ones that passed — is stored as one row, so this page is a
 * table of evidence rather than a summary that cannot be re-read.
 *
 * The page never claims a header is present because the deployment is supposed to set it: each
 * row shows the evidence string the scanner actually saw, and a missing header says "ไม่พบ".
 */

type CheckStatus = 'pass' | 'warn' | 'fail' | 'info'

interface EdgeScan {
  id: string
  url: string
  final_url: string
  host: string
  grade: string
  score: number
  status: CheckStatus
  http_status: number
  http_proto: string
  tls_version: string
  tls_cipher: string
  tls_alpn: string
  tls_cert_subject: string
  tls_cert_issuer: string
  tls_cert_not_after?: string
  tls_cert_days: number
  redirect_count: number
  duration_ms: number
  checks_total: number
  checks_passed: number
  checks_warned: number
  checks_failed: number
  checks_noted: number
  scanned_by_role: string
  created_at: string
}

interface EdgeFinding {
  id: string
  scan_id: string
  check_id: string
  category: 'transport' | 'headers' | 'redirects' | 'cookies'
  title: string
  status: CheckStatus
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical'
  evidence: string
  remediation: string
  weight: number
  deduction: number
  position: number
  created_at: string
}

interface EdgeDetail {
  scan: EdgeScan
  findings: EdgeFinding[]
}

interface EdgeTLS {
  version_name: string
  cipher_name: string
  alpn: string
  certificate_subject: string
  certificate_issuer: string
  not_after: string
  days_remaining: number
  verified: boolean
}

interface EdgeReport {
  grade: string
  score: number
  status: CheckStatus
  summary: { total: number; passed: number; warned: number; failed: number; noted: number }
  tls?: EdgeTLS
  redirects: { from: string; to: string; status: number; tls_version?: string; cross_site: boolean; downgrade: boolean; truncated: boolean }[]
  cookies: { name: string; secure: boolean; http_only: boolean; same_site: string; set_at_url: string }[]
}

interface PolicyView {
  min_tls_version: string
  preferred_tls_version: string
  min_hsts_max_age_days: number
  max_redirects: number
  required_headers: string[]
  grading: { grade: string; min_score: number }[]
}

const SCAN_KEY = ['edge-audit', 'scans'] as const
const CATEGORY_LABEL: Record<EdgeFinding['category'], string> = {
  transport: 'TLS',
  headers: 'ส่วนหัว',
  redirects: 'เปลี่ยนเส้นทาง',
  cookies: 'คุกกี้',
}

function gradeTone(grade: string): string {
  if (grade === 'A+' || grade === 'A') return 'var(--color-emerald-soft)'
  if (grade === 'B' || grade === 'C') return 'var(--color-gold)'
  if (grade === 'D') return 'var(--color-amber)'
  return 'var(--color-crimson)'
}

function statusTone(status: CheckStatus): string {
  switch (status) {
    case 'pass':
      return 'var(--color-emerald-soft)'
    case 'warn':
      return 'var(--color-amber)'
    case 'fail':
      return 'var(--color-crimson)'
    default:
      return 'var(--color-ivory-dim)'
  }
}

function StatusChip({ status }: { status: CheckStatus }) {
  const label: Record<CheckStatus, string> = {
    pass: 'ผ่าน',
    warn: 'ควรปรับปรุง',
    fail: 'ไม่ผ่าน',
    info: 'ข้อมูล',
  }
  return (
    <span className="chip" style={{ color: statusTone(status), borderColor: statusTone(status) }}>
      {label[status]}
    </span>
  )
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function ScanResult({ detail }: { detail: EdgeDetail }) {
  const { scan, findings } = detail
  const failing = findings.filter((f) => f.status === 'fail' || f.status === 'warn')

  return (
    <div className="stack">
      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <div className="card-label">ผลการสแกน</div>
            <div className="row" style={{ gap: 14, alignItems: 'baseline' }}>
              <span style={{ fontSize: 54, lineHeight: 1, color: gradeTone(scan.grade), fontVariantNumeric: 'tabular-nums' }}>
                {scan.grade}
              </span>
              <span className="mono" style={{ fontSize: 15 }}>{scan.score}/100</span>
              <StatusChip status={scan.status} />
            </div>
            <div className="mono dim" style={{ marginTop: 8, wordBreak: 'break-all' }}>{scan.final_url || scan.url}</div>
            <div className="row muted" style={{ gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
              <span>{thaiDateTime(scan.created_at)}</span>
              <span>·</span>
              <span className="mono">{scan.http_proto || 'HTTP'} {scan.http_status}</span>
              <span>·</span>
              <span>{scan.duration_ms} ms</span>
              <span>·</span>
              <span>ผู้สแกน {scan.scanned_by_role || 'ไม่ระบุ'}</span>
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span className="chip" style={{ color: 'var(--color-emerald-soft)' }}>{scan.checks_passed} ผ่าน</span>
            <span className="chip" style={{ color: 'var(--color-amber)' }}>{scan.checks_warned} ควรปรับปรุง</span>
            <span className="chip" style={{ color: 'var(--color-crimson)' }}>{scan.checks_failed} ไม่ผ่าน</span>
            <span className="chip">{scan.checks_total} รายการ</span>
          </div>
        </div>

        <div className="grid-3" style={{ marginTop: 18 }}>
          <div>
            <div className="card-label">เวอร์ชัน TLS</div>
            <div className="mono" style={{ color: scan.tls_version === 'TLS 1.3' ? 'var(--color-emerald-soft)' : 'var(--color-ivory)' }}>
              {scan.tls_version || 'ไม่ใช้ TLS'}
            </div>
            <div className="dim" style={{ fontSize: 11.5 }}>ต้องการอย่างน้อย {scan.tls_version ? 'TLS 1.2' : 'HTTPS'}</div>
          </div>
          <div>
            <div className="card-label">ชุดรหัสลับ</div>
            <div className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{scan.tls_cipher || '—'}</div>
            <div className="dim" style={{ fontSize: 11.5 }}>ALPN {scan.tls_alpn || '—'}</div>
          </div>
          <div>
            <div className="card-label">ใบรับรอง</div>
            <div style={{ fontSize: 12.5 }}>{scan.tls_cert_subject || '—'}</div>
            <div className="dim" style={{ fontSize: 11.5 }}>
              {scan.tls_cert_not_after ? `หมดอายุ ${thaiDate(scan.tls_cert_not_after)} · เหลือ ${scan.tls_cert_days} วัน` : 'ไม่พบข้อมูลใบรับรอง'}
            </div>
          </div>
        </div>
      </div>

      <div className="card card-pad">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label" style={{ marginBottom: 0 }}>รายการตรวจทั้งหมด</div>
          <span className="dim" style={{ fontSize: 12 }}>เรียงตามลำดับที่ให้คะแนน</span>
        </div>
        <div className="table-wrap" style={{ border: 0 }}>
          <table className="data">
            <thead>
              <tr>
                <th>สถานะ</th>
                <th>รายการ</th>
                <th>หมวด</th>
                <th>ความรุนแรง</th>
                <th>หักคะแนน</th>
                <th>หลักฐานที่ตรวจพบ</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((finding) => (
                <tr key={finding.id} style={{ cursor: 'default' }}>
                  <td><StatusChip status={finding.status} /></td>
                  <td style={{ minWidth: 180 }}>
                    <div>{finding.title}</div>
                    <div className="mono muted" style={{ fontSize: 11 }}>{finding.check_id}</div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{CATEGORY_LABEL[finding.category]}</td>
                  <td>
                    {finding.severity === 'info'
                      ? <span className="dim">—</span>
                      : <Severity value={finding.severity} />}
                  </td>
                  <td className="mono" style={{ color: finding.deduction > 0 ? 'var(--color-amber)' : 'var(--color-ivory-dim)' }}>
                    {finding.deduction > 0 ? `-${finding.deduction}` : '0'}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    <div style={{ fontSize: 12.5 }}>{finding.evidence || '—'}</div>
                    {finding.status !== 'pass' && finding.remediation ? (
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>แนวทางแก้ไข: {finding.remediation}</div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {scan.redirect_count > 0 ? (
        <div className="card card-pad">
          <div className="card-label">การเปลี่ยนเส้นทางที่บันทึกไว้ ({scan.redirect_count} hop)</div>
          <div className="stack" style={{ gap: 8 }}>
            <div className="dim" style={{ fontSize: 12 }}>
              ระบบไม่ตามการเปลี่ยนเส้นทางอัตโนมัติ แต่บันทึกทุก hop แล้วให้คะแนนจากนโยบาย
            </div>
            <div className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>
              {scan.url}
            </div>
            <div className="dim" style={{ fontSize: 11.5 }}>↓</div>
            <div className="mono" style={{ fontSize: 11.5, wordBreak: 'break-all' }}>{scan.final_url}</div>
          </div>
        </div>
      ) : null}

      {failing.length > 0 ? (
        <div className="card card-pad">
          <div className="card-label">สิ่งที่ต้องแก้ไขก่อนสแกนครั้งถัดไป</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
            {failing.map((finding) => (
              <li key={`fix-${finding.id}`}>{finding.title} — {finding.remediation}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export function EdgeAudit() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const role: Role = me?.role ?? 'auditor'
  const canScan = role === 'owner' || role === 'admin' || role === 'audit_manager'

  const [target, setTarget] = useState('https://auditai-edge.toojinda2.workers.dev')
  const [selected, setSelected] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const scans = useQuery({
    queryKey: SCAN_KEY,
    queryFn: () => list<EdgeScan>('/v1/edge-audit/scans?limit=50'),
    staleTime: 15_000,
  })

  const policy = useQuery({
    queryKey: ['edge-audit', 'policy'],
    queryFn: () => api<PolicyView>('/v1/edge-audit/policy'),
    staleTime: Infinity,
  })

  const detail = useQuery({
    queryKey: ['edge-audit', 'scan', selected],
    queryFn: () => api<EdgeDetail>(`/v1/edge-audit/scans/${selected}`),
    enabled: selected !== '',
    staleTime: 60_000,
  })

  const run = useMutation({
    mutationFn: () => apiWrite<{ scan: EdgeScan; report: EdgeReport }>('/v1/edge-audit/scans', 'POST', {
      url: target.trim(),
    }),
    onSuccess: (result) => {
      setError('')
      setNotice(`สแกน ${result.scan.host} เสร็จแล้ว · เกรด ${result.scan.grade} · ${result.scan.score}/100`)
      setSelected(result.scan.id)
      void qc.invalidateQueries({ queryKey: SCAN_KEY })
    },
    onError: (err) => {
      setNotice('')
      setError(errorText(err, 'สแกนไม่สำเร็จ'))
    },
  })

  const rows = scans.data ?? []
  const active = detail.data
  const policyRows = policy.data
  const requiredHeaders = useMemo(() => policyRows?.required_headers ?? [], [policyRows])

  function submit() {
    setError('')
    setNotice('')
    if (!target.trim()) {
      setError('กรุณาระบุ URL ที่ต้องการสแกน')
      return
    }
    run.mutate()
  }

  return (
    <>
      <PageHead
        title="ตรวจความปลอดภัยของ Edge"
        sub="สแกน HTTPS จริงของโฮสต์ที่ให้บริการ แล้วให้เกรดจาก TLS ส่วนหัวความปลอดภัย การเปลี่ยนเส้นทาง และคุกกี้ · ผลทุกครั้งถูกเก็บเป็นหลักฐานแบบแก้ไขไม่ได้"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{policyRows?.preferred_tls_version ?? 'TLS 1.3'} เป็นเป้าหมาย</span>
        <span className="chip">HSTS อย่างน้อย {policyRows?.min_hsts_max_age_days ?? 180} วัน</span>
        <span className="chip">เปลี่ยนเส้นทางไม่เกิน {policyRows?.max_redirects ?? 3} hop</span>
        <span className="chip">{requiredHeaders.length || 6} ส่วนหัวที่ตรวจ</span>
        <span className="chip">{rows.length} การสแกนในประวัติ</span>
      </div>

      <div className="grid-main">
        <div className="stack">
          {detail.isLoading ? (
            <div className="card card-pad stack"><Skel h={120} /><Skel h={220} /></div>
          ) : active ? (
            <ScanResult detail={active} />
          ) : (
            <div className="card card-pad">
              <Empty
                title="ยังไม่ได้เลือกผลการสแกน"
                body="สแกนโฮสต์แรกจากแบบฟอร์มด้านขวา หรือเลือกครั้งก่อนจากประวัติ ระบบจะแสดงตารางผ่าน/ไม่ผ่านของทุกส่วนหัวที่ตรวจ พร้อมหลักฐานที่เห็นจริงและแนวทางแก้ไข"
              />
            </div>
          )}
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">สแกนโฮสต์ใหม่</div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="edge-target">URL ปลายทาง</label>
              <input
                id="edge-target"
                className="input"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="https://auditai-edge.toojinda2.workers.dev"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={submit} disabled={!canScan || run.isPending}>
                {run.isPending ? 'กำลังสแกน…' : 'สแกนเดี๋ยวนี้'}
              </button>
              <span className="dim" style={{ fontSize: 11.5 }}>สูงสุด 8 วินาที</span>
            </div>
            {!canScan ? (
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                บทบาทของคุณอ่านผลได้ แต่การสแกนจำกัดเฉพาะ owner, admin และ audit_manager
              </div>
            ) : null}
            {notice ? <div style={{ color: 'var(--color-emerald-soft)', fontSize: 12, marginTop: 10 }}>{notice}</div> : null}
            {error ? <div style={{ color: 'var(--color-crimson)', fontSize: 12, marginTop: 10 }}>{error}</div> : null}

            <div className="rule" style={{ margin: '16px 0' }} />
            <div className="stack" style={{ gap: 6, fontSize: 11.5 }}>
              <span className="muted">คำขอ HTTPS จริงจากเซิร์ฟเวอร์ ไม่ใช่การเดาจากการตั้งค่า</span>
              <span className="muted">บันทึกทุก hop แต่ไม่ตามการเปลี่ยนเส้นทางอัตโนมัติ</span>
              <span className="muted">ปฏิเสธที่อยู่ภายใน ลูปแบ็ก และ metadata เพื่อกัน SSRF</span>
              <span className="muted">ผลถูกบันทึกแบบเพิ่มอย่างเดียว แก้ไขหรือลบย้อนหลังไม่ได้</span>
            </div>
          </div>

          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>ประวัติการสแกน</div>
              <span className="dim" style={{ fontSize: 11.5 }}>ล่าสุดก่อน</span>
            </div>

            {scans.isLoading ? (
              <div className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
            ) : scans.isError ? (
              <div className="dim" style={{ fontSize: 12 }}>
                ยังโหลดประวัติไม่ได้: {errorText(scans.error, 'เส้นทางยังไม่ถูกเปิดใช้งาน')}
              </div>
            ) : rows.length === 0 ? (
              <Empty
                title="ยังไม่มีประวัติ"
                body="เมื่อสแกนครั้งแรก ผลและรายการตรวจทั้งหมดจะถูกเก็บไว้ที่นี่ ทุกครั้งสร้างแถวใหม่ ไม่ทับผลเดิม"
              />
            ) : (
              <div className="table-wrap" style={{ border: 0 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>โฮสต์</th>
                      <th>เกรด</th>
                      <th>TLS</th>
                      <th>ไม่ผ่าน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelected(row.id)}
                        style={{ background: row.id === selected ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                      >
                        <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{thaiDateTime(row.created_at)}</td>
                        <td style={{ fontSize: 12 }}>{row.host}</td>
                        <td>
                          <span style={{ color: gradeTone(row.grade), fontWeight: 500 }}>{row.grade}</span>
                          <span className="mono dim" style={{ fontSize: 11, marginLeft: 6 }}>{row.score}</span>
                        </td>
                        <td className="mono" style={{ fontSize: 11 }}>{row.tls_version || '—'}</td>
                        <td style={{ fontSize: 12, color: row.checks_failed > 0 ? 'var(--color-crimson)' : 'var(--color-emerald-soft)' }}>
                          {row.checks_failed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {policyRows ? (
            <div className="card card-pad">
              <div className="card-label">นโยบายที่ใช้ให้คะแนน</div>
              <div className="stack" style={{ gap: 6, fontSize: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="muted">TLS ขั้นต่ำ</span>
                  <span className="mono">{policyRows.min_tls_version}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="muted">HSTS ขั้นต่ำ</span>
                  <span className="mono">{policyRows.min_hsts_max_age_days} วัน</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="muted">เปลี่ยนเส้นทางสูงสุด</span>
                  <span className="mono">{policyRows.max_redirects} hop</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span className="muted">เกณฑ์เกรด</span>
                  <span className="mono" style={{ textAlign: 'right', fontSize: 11 }}>
                    {policyRows.grading.map((band) => `${band.grade} ≥ ${band.min_score}`).join(' · ')}
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
